import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import './App.css';
import {
  clearTokens,
  dismissFeedbackPrompt,
  fetchCapabilities,
  fetchUserProfile,
  isAuthenticated as hasToken,
} from './api';
import { setLanguageFromLocale } from './i18n';
import { CapabilitiesContext } from './CapabilitiesContext';
import { PreferredLocaleContext } from './PreferredLocaleContext';
import { SpendingCapContext } from './SpendingCapContext';
import { FeedbackInvitationContext } from './FeedbackInvitationContext';
import { FEEDBACK_PATH } from './feedbackScreen';
import About from './About';
import Chat from './Chat';
import ChatList, { NewChatPage } from './ChatList';
import Feedback from './Feedback';
import Feedbacks from './Feedbacks';
import FeedbackInvitationBanner from './FeedbackInvitationBanner';
import Login from './Login';
import PersonaBuilder from './PersonaBuilder';
import PersonaList from './PersonaList';
import NewPersonaPage from './PersonaForm';
import Register from './Register';
import Profile from './Profile';
import ScenarioDoor from './ScenarioDoor';
import ScenarioList from './ScenarioList';
import ScenarioPlayer from './ScenarioPlayer';
import SceneRecord from './SceneRecord';
import SceneVoice from './SceneVoice';
import Sidebar from './Sidebar';
import { SCENARIO_PATH, SCENARIO_RUN_PATH, SCENARIOS_PATH } from './scenes';
import Voice from './Voice';

// Wraps a page with the app-wide navigation sidebar. Used for every screen
// except the Chat conversation view, which needs the full width for the
// exchange, and the anonymous login/register pages.
//
// [wide] is a screen saying it is to be given the window rather than left at the width of its own
// contents, which is what the space the routes sit in would otherwise settle on. A scene asks for
// it: a place is as wide as the room it is played in, however little is on the screen.
function AppLayout({ onLogout, wide = false, children }) {
  return (
    <div className={wide ? 'app-layout app-layout--wide' : 'app-layout'}>
      <Sidebar onLogout={onLogout} />
      {children}
    </div>
  );
}

// The router's half of the voice screen, and all of it. Voice takes the persona as a prop so that
// it stays reachable from a test without one — the screen is otherwise all microphones and sockets,
// and a router on top of those is a mock nobody needs.
function VoiceRoute() {
  const { personaId } = useParams();
  return <Voice personaId={personaId} />;
}

function App() {
  // Auth state is seeded from the JWT tokens persisted in localStorage, so a page
  // reload keeps the session.
  const [isAuthenticated, setIsAuthenticated] = useState(hasToken);
  // The signed-in user's preferred language (a full LocaleDto), shared through
  // context so the chat "I speak" dropdowns can default to it. Null until the
  // profile is fetched or when the user has not set one.
  const [preferredLocale, setPreferredLocale] = useState(null);
  // Whether the user has spent their AI allowance, and the reason the backend gave (#208).
  // Seeded from the profile so a reload does not forget, then kept current by the 402s.
  const [capped, setCapped] = useState(false);
  const [cappedMessage, setCappedMessage] = useState('');
  // Whether the backend is inviting this user to give feedback (#212). Seeded from the profile
  // so the ask survives a reload and finds them on any device, then raised by the answer to the
  // message that crosses the threshold. The browser never counts messages itself.
  const [invited, setInvited] = useState(false);
  // Whether this deployment serves the character stub, and with it the screens that only exist
  // beside it. The app is built once and deployed everywhere, so it asks rather than assumes.
  // Null until the answer arrives, which is neither a yes nor a no.
  const [characterStub, setCharacterStub] = useState(null);
  const navigate = useNavigate();

  const handleLogin = () => {
    setIsAuthenticated(true);
    navigate('/chats');
  };

  const handleRegister = () => {
    setIsAuthenticated(true);
    navigate('/chats');
  };

  const handleLogout = useCallback(() => {
    clearTokens();
    setIsAuthenticated(false);
    setPreferredLocale(null);
    // The cap belongs to the user who just left: whoever signs in next gets their own from
    // their profile, so carrying this over would disable AI for someone with allowance left.
    setCapped(false);
    setCappedMessage('');
    // Likewise the invitation: it was addressed to the user who just left.
    setInvited(false);
    navigate('/login');
  }, [navigate]);

  // Answering the banner is a decision worth remembering, so it goes to the server rather than
  // to this tab's memory. The banner closes on the click and does not wait for the round trip: if
  // the call fails the ask simply returns on the next load, which beats blocking the user here.
  const dismissInvitation = useCallback(() => {
    setInvited(false);
    dismissFeedbackPrompt().catch(() => {
      // Ignore: the server did not record it, so the invitation comes back. Nothing is lost.
    });
  }, []);

  // The user answered the ask, so there is nothing left to ask. The submission already told the
  // server, which will not invite them again; this only spares the session a banner still asking
  // for what it has just been given, since nothing refetches the profile until a reload.
  const markInvitationGiven = useCallback(() => setInvited(false), []);

  // Once authenticated, load the profile so the UI can adopt the user's stored
  // preferred language (it wins over the browser/localStorage guess) and the chat
  // dropdowns can default the student locale to it. Best-effort: a failure just
  // leaves the locally detected language in place.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let cancelled = false;
    fetchUserProfile()
      .then((profile) => {
        if (cancelled) {
          return;
        }
        // The backend is the authority on the cap, so a reload (or a second tab) starts out
        // knowing, instead of having to spend a doomed AI request to find out.
        setCapped(!!profile.aiCapped);
        // The same reasoning for the invitation: the server decided it from the messages the user
        // really sent, so a reload or a second device inherits the ask instead of starting over.
        setInvited(!!profile.feedbackInvitation);
        if (!profile.preferredLocale) {
          return;
        }
        setPreferredLocale(profile.preferredLocale);
        setLanguageFromLocale(profile.preferredLocale);
      })
      .catch(() => {
        // Ignore: keep the language detected from localStorage/browser.
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // What this deployment serves, read once per session. A failure answers no: a development-only
  // screen offered to a browser that could not be told it is one dead-ends on the first call.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let cancelled = false;
    fetchCapabilities()
      .then((capabilities) => {
        if (!cancelled) {
          setCharacterStub(!!capabilities.characterStub);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCharacterStub(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // authedFetch dispatches 'auth:logout' when a token refresh ultimately fails, so
  // an expired session transparently routes the user back to the login page.
  useEffect(() => {
    const onLogout = () => handleLogout();
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, [handleLogout]);

  // The same shape for the other refusal authedFetch recognises: a 402 means the user's AI
  // allowance is gone (#206), which is the moment worth asking them what it was worth (#208).
  // Handling it here rather than per screen is what lets any AI call, from anywhere, close
  // the funnel. The reason travels with the event so the user is told why, not just moved.
  useEffect(() => {
    const onCapped = (e) => {
      setCapped(true);
      setCappedMessage(e.detail?.message || '');
      navigate(FEEDBACK_PATH);
    };
    window.addEventListener('ai:capped', onCapped);
    return () => window.removeEventListener('ai:capped', onCapped);
  }, [navigate]);

  // The answer to a message says whether that message earned the invitation (#212). Chat announces
  // it the same way, so the banner appears without a reload and without the chat screen having to
  // own an app-wide concern. Unlike the cap, this moves nobody: it is an offer, mid-lesson.
  useEffect(() => {
    const onInvited = () => setInvited(true);
    window.addEventListener('feedback:invited', onInvited);
    return () => window.removeEventListener('feedback:invited', onInvited);
  }, []);

  // Protects the authenticated screens: sends visitors without a token back to the
  // login page (this also fires after a reload when no token is stored).
  const requireAuth = (element) =>
    isAuthenticated ? element : <Navigate to="/login" replace />;

  // Protects the screens that only exist where the character stub is served: elsewhere they are
  // sent to the chats, as any unknown path is. Nothing at all while the capability is in flight —
  // a screen shown and then taken away is worse than one that arrives a moment late.
  const requireCharacterStub = (element) => {
    if (characterStub === null) {
      return null;
    }
    return characterStub ? element : <Navigate to="/chats" replace />;
  };

  // Keeps the login/register pages out of reach once authenticated.
  const requireAnonymous = (element) =>
    isAuthenticated ? <Navigate to="/chats" replace /> : element;

  return (
    <PreferredLocaleContext.Provider value={{ preferredLocale, setPreferredLocale }}>
    {/* Read once per session above the routes, because more than one screen turns on it and
        none of them should ask again: the door offers a development control, the scenes stand
        a character in. In flight it reads as false, which shows a student nothing. */}
    <CapabilitiesContext.Provider value={{ characterStub: characterStub === true }}>
    <SpendingCapContext.Provider value={{ capped, cappedMessage }}>
    <FeedbackInvitationContext.Provider
      value={{ invited, dismiss: dismissInvitation, markGiven: markInvitationGiven }}
    >
    <div className="App">
      <Toaster position="bottom-center" richColors closeButton />
      {/* Above the routes rather than inside a layout: the chat conversation deliberately skips
          AppLayout, and that is the screen where the user has most to say. */}
      {isAuthenticated && <FeedbackInvitationBanner />}
      <div className="App__content">
      <Routes>
        <Route
          path="/login"
          element={requireAnonymous(<Login onLogin={handleLogin} />)}
        />
        <Route
          path="/register"
          element={requireAnonymous(<Register onRegister={handleRegister} />)}
        />
        <Route
          path="/chats"
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <ChatList />
            </AppLayout>
          )}
        />
        <Route
          path="/chats/new"
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <NewChatPage />
            </AppLayout>
          )}
        />
        <Route
          path="/personas"
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <PersonaList />
            </AppLayout>
          )}
        />
        <Route
          path="/personas/new"
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <NewPersonaPage />
            </AppLayout>
          )}
        />
        <Route
          path="/personas/build"
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <PersonaBuilder />
            </AppLayout>
          )}
        />
        <Route
          path="/profile"
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <Profile onLogout={handleLogout} />
            </AppLayout>
          )}
        />
        {/* No sidebar entry: a voice session is always against a persona, so it is reached from
            the persona list. A bare /voice names nobody and falls through to the catch-all. */}
        <Route
          path="/voice/:personaId"
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <VoiceRoute />
            </AppLayout>
          )}
        />
        <Route
          path={FEEDBACK_PATH}
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <Feedback />
            </AppLayout>
          )}
        />
        <Route
          path="/feedbacks"
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <Feedbacks />
            </AppLayout>
          )}
        />
        <Route
          path="/chats/:chatId"
          element={requireAuth(<Chat />)}
        />
        {/* The record of what was played, and the one screen under Scenes that keeps the
            sidebar: it is read like the chat list, not played like a scene. */}
        <Route
          path="/scenes"
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <SceneRecord />
            </AppLayout>
          )}
        />
        {/* What there is to play, read like a list and so keeping the sidebar. Where the player
            sends a student who has finished a run, and where Play sends one who has not started. */}
        <Route
          path={SCENARIOS_PATH}
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <ScenarioList />
            </AppLayout>
          )}
        />
        {/* The door of one scenario: what it walks, and where in that walk a run of it starts.
            Read like a list and so keeping the sidebar, as the list it is picked from does — the
            student is still choosing here, and nothing has been bought yet. */}
        <Route
          path={SCENARIO_PATH}
          element={requireAuth(
            <AppLayout onLogout={handleLogout}>
              <ScenarioDoor />
            </AppLayout>
          )}
        />
        {/* No sidebar: the player takes the window and wears the palette of the scene it is
            holding, which a themed sidebar beside it would fight. The chat conversation skips the
            layout for its own reasons, just above. */}
        {/* The run's own path, which is what the door sends the student to and what a reload comes
            back through. A key nothing is played under is answered by the player itself. */}
        <Route
          path={SCENARIO_RUN_PATH}
          element={requireAuth(<ScenarioPlayer />)}
        />
        {/* Speaking to one character of one scene, by the part they play rather than by the
            persona cast in it. Scene-generic, which is what makes a scene playable before
            it has a screen of its own — and, for that reason, served only where the character
            stub is: a scene with a screen of its own is played there. */}
        <Route
          path="/scenes/:scenarioKey/voice/:role"
          element={requireAuth(
            requireCharacterStub(
              <AppLayout onLogout={handleLogout} wide>
                <SceneVoice />
              </AppLayout>
            )
          )}
        />
        {/* The About page is public: version and release notes need no session,
            so the sidebar only offers Logout when a session actually exists. */}
        <Route
          path="/about"
          element={
            <AppLayout onLogout={isAuthenticated ? handleLogout : undefined}>
              <About />
            </AppLayout>
          }
        />
        <Route path="*" element={<Navigate to="/chats" replace />} />
      </Routes>
      </div>
    </div>
    </FeedbackInvitationContext.Provider>
    </SpendingCapContext.Provider>
    </CapabilitiesContext.Provider>
    </PreferredLocaleContext.Provider>
  );
}

export default App;
